import type { DesignFamilyId } from "../../design-rules/select-design-family.js";

export type ModernBusinessTheme = {
  id: DesignFamilyId;
  label: string;
  variables: Record<string, string>;
};

export const MODERN_BUSINESS_THEMES: Record<
  DesignFamilyId,
  ModernBusinessTheme
> = {
  "modern-blue-v1": {
    id: "modern-blue-v1",
    label: "Blue",
    variables: {
      "--ink": "#172033",
      "--muted": "#667085",
      "--paper": "#f7faff",
      "--pearl": "#ffffff",
      "--pearl-deep": "#edf4ff",
      "--white": "#ffffff",
      "--peach": "#3474dc",
      "--peach-dark": "#2863c0",
      "--peach-soft": "#eaf2ff",
      "--gold": "#9fc0ef",
      "--line": "#dce7f7",
      "--shadow": "0 18px 50px rgba(23,32,51,.10)",
      "--hero-glow": "#cfe1ff",
      "--hero-end": "#ffffff",
      "--button-text": "#172033",
    },
  },

  "modern-sage-v1": {
    id: "modern-sage-v1",
    label: "Sage",
    variables: {
      "--ink": "#20271d",
      "--muted": "#687063",
      "--paper": "#fafbf7",
      "--pearl": "#ffffff",
      "--pearl-deep": "#f1f4eb",
      "--white": "#ffffff",
      "--peach": "#667d42",
      "--peach-dark": "#566b36",
      "--peach-soft": "#edf2e4",
      "--gold": "#a9b98d",
      "--line": "#dde4d5",
      "--shadow": "0 18px 50px rgba(32,39,29,.09)",
      "--hero-glow": "#dfe8cf",
      "--hero-end": "#ffffff",
      "--button-text": "#20271d",
    },
  },

  "modern-soft-pink-v1": {
    id: "modern-soft-pink-v1",
    label: "Soft Pink",
    variables: {
      "--ink": "#2a2323",
      "--muted": "#746766",
      "--paper": "#fffaf9",
      "--pearl": "#ffffff",
      "--pearl-deep": "#fcf0ee",
      "--white": "#ffffff",
      "--peach": "#d98e86",
      "--peach-dark": "#c87e76",
      "--peach-soft": "#fae8e5",
      "--gold": "#efb8b1",
      "--line": "#efd8d4",
      "--shadow": "0 18px 50px rgba(58,45,45,.09)",
      "--hero-glow": "#f6d6d2",
      "--hero-end": "#ffffff",
      "--button-text": "#2a2323",
    },
  },

  "modern-navy-v1": {
    id: "modern-navy-v1",
    label: "Navy",
    variables: {
      "--ink": "#172033",
      "--muted": "#647081",
      "--paper": "#f8fafc",
      "--pearl": "#ffffff",
      "--pearl-deep": "#edf2f7",
      "--white": "#ffffff",
      "--peach": "#193354",
      "--peach-dark": "#122943",
      "--peach-soft": "#e9eef5",
      "--gold": "#91a8c2",
      "--line": "#dce3eb",
      "--shadow": "0 18px 50px rgba(16,35,61,.10)",
      "--hero-glow": "#d7e2ef",
      "--hero-end": "#ffffff",
      "--button-text": "#ffffff",
    },
  },

  "modern-charcoal-v1": {
    id: "modern-charcoal-v1",
    label: "Charcoal",
    variables: {
      "--ink": "#202020",
      "--muted": "#6c6964",
      "--paper": "#faf9f7",
      "--pearl": "#ffffff",
      "--pearl-deep": "#f1f0ed",
      "--white": "#ffffff",
      "--peach": "#292929",
      "--peach-dark": "#171717",
      "--peach-soft": "#eceae6",
      "--gold": "#aaa49b",
      "--line": "#e1ded8",
      "--shadow": "0 18px 50px rgba(32,32,32,.09)",
      "--hero-glow": "#dedbd6",
      "--hero-end": "#ffffff",
      "--button-text": "#ffffff",
    },
  },
};

export function themeCss(familyId: DesignFamilyId): string {
  const theme =
    MODERN_BUSINESS_THEMES[familyId] ??
    MODERN_BUSINESS_THEMES["modern-blue-v1"];

  const declarations = Object.entries(theme.variables)
    .map(([name, value]) => `${name}:${value};`)
    .join("");

  return `:root{${declarations}}`;
}
